import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Heart, Package, User, LogOut, Shield, Search } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const DEEP = "#1E40AF";     // header/footer bg, on-white text
const MUTED = "#C7D2F5";    // small labels
const DIVIDER = "#DCE4FB";  // search input/button divider

function Logo({ testid = "logo-link" }) {
  return (
    <Link to="/" data-testid={testid} className="flex shrink-0 items-center">
      <img src="/logo.png" alt="SellStuff" className="h-[39px] w-auto sm:h-[45px]" />
    </Link>
  );
}

function SearchBar({ idSuffix = "" }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => { e.preventDefault(); navigate(`/?search=${encodeURIComponent(q.trim())}`); };
  return (
    <form onSubmit={submit} className="flex w-full items-stretch overflow-hidden rounded-full bg-white">
      <input
        data-testid={`search-input${idSuffix}`}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search headphones, iPhone, bags"
        className="h-10 w-full border-0 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
      />
      <button type="submit" data-testid={`search-btn${idSuffix}`} aria-label="Search" className="flex h-10 w-12 items-center justify-center bg-white" style={{ borderLeft: `1px solid ${DIVIDER}` }}>
        <Search className="h-4 w-4" strokeWidth={3} style={{ color: DEEP }} />
      </button>
    </form>
  );
}

function CursorTap() {
  return (
    <span className="ss-cursor-tap pointer-events-none absolute -bottom-1.5 -right-1.5" aria-hidden="true">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="#FFFFFF" stroke={DEEP} strokeWidth="1.5" strokeLinejoin="round"><path d="M4 2l6 16 2.5-6.5L19 9 4 2z" /></svg>
    </span>
  );
}

function NotifBell({ notifs, unread, markRead, navigate }) {
  return (
    <DropdownMenu onOpenChange={(o) => o && unread && markRead()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative rounded-full p-2 hover:bg-white/10" aria-label="Notifications">
          <Bell className="h-5 w-5 text-white" />
          {unread > 0 && <span data-testid="notif-badge" className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-red-500" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 rounded-xl p-0">
        <div className="border-b p-3 font-heading font-semibold">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {notifs.length === 0 && <div className="p-4 text-sm text-slate-500">No notifications yet.</div>}
          {notifs.map((n) => (
            <div key={n.id} onClick={() => n.link && navigate(n.link)} className={`border-b p-3 text-sm last:border-0 ${n.link ? "cursor-pointer hover:bg-slate-50" : ""}`} data-testid="notif-item">
              <div className="font-medium text-slate-900">{n.title}</div>
              <div className="text-slate-500">{n.message}</div>
              {n.action?.kind === "collection_slot" && <button data-testid="dd-notif-collection" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=sales"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Select Collection Slot</button>}
              {n.action?.kind === "delivery_slot" && <button data-testid="dd-notif-delivery" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=purchases"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Select Delivery Slot</button>}
              {n.action?.kind === "view_order" && <button data-testid="dd-notif-view-order" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=sales"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">View Order</button>}
              {n.action?.kind === "review_kyc" && <button data-testid="dd-notif-review-kyc" onClick={(e) => { e.stopPropagation(); navigate(n.link || "/admin"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Review KYC</button>}
              {n.action?.kind === "return_slot_buyer" && <button data-testid="dd-notif-return-buyer" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=purchases"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Select Return Slot</button>}
              {n.action?.kind === "return_slot_seller" && <button data-testid="dd-notif-return-seller" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=sales"); }} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">Select return slot</button>}
              {n.action?.kind === "approve_or_concern" && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button data-testid="dd-notif-approve" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=purchases"); }} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-medium text-white">Confirm I'm happy with my purchase</button>
                  <button data-testid="dd-notif-concern" onClick={(e) => { e.stopPropagation(); navigate("/orders?tab=purchases"); }} className="rounded-full border border-red-300 px-3 py-1 text-xs font-medium text-red-600">Raise a Concern</button>
                </div>
              )}
            </div>
          ))}
        </div>
        <Link to="/notifications" className="block border-t p-3 text-center text-sm font-medium text-primary" data-testid="notif-view-all">View all notifications</Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Avatar({ user, navigate, logout }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="user-menu" className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold uppercase" style={{ color: DEEP }} aria-label="Account">
          {user.name?.[0] || "U"}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-xl">
        <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders"><Package className="mr-2 h-4 w-4" /> Orders</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notifications"><Bell className="mr-2 h-4 w-4" /> Notifications</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/watchlist")}><Heart className="mr-2 h-4 w-4" /> Watchlist</DropdownMenuItem>
        {user.is_admin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="mr-2 h-4 w-4" /> Admin</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" /> Log out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!user) return;
    const load = async () => { try { const { data } = await api.get("/notifications"); setNotifs(data); } catch { /* ignore */ } };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);

  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => { await api.put("/notifications/read-all"); setNotifs((p) => p.map((n) => ({ ...n, read: true }))); };

  const styleTag = (
    <style>{`
      @keyframes ss-cursor-tap { 0%,70%,100% { transform: translate(0,0) scale(1);} 82% { transform: translate(-1px,1px) scale(0.82);} }
      .ss-cursor-tap { animation: ss-cursor-tap 1.5s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) { .ss-cursor-tap { animation: none; } }
    `}</style>
  );

  if (user?.is_admin) {
    return (
      <header className="sticky top-0 z-50" style={{ backgroundColor: DEEP }} data-testid="site-header">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white" data-testid="admin-badge">Admin</span>
            <Button variant="ghost" className="rounded-full font-medium text-white hover:bg-white/10" data-testid="admin-logout" onClick={() => { logout(); navigate("/admin"); }}>
              <LogOut className="mr-1 h-4 w-4" /> Log out
            </Button>
          </div>
        </div>
      </header>
    );
  }

  const LoggedOutIcons = ({ mobile }) => (
    <>
      <Link to="/login" data-testid={mobile ? "nav-login-mobile" : "nav-login"} className="text-sm font-medium text-white hover:text-white/80">Log In</Link>
      {!mobile && (
        <Button asChild variant="outline" data-testid="nav-register" className="h-9 rounded-full border border-white bg-transparent font-medium text-white hover:bg-white/10">
          <Link to="/register">Sign Up</Link>
        </Button>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50" style={{ backgroundColor: DEEP }} data-testid="site-header">
      {styleTag}
      {/* DESKTOP (>=1024px) */}
      <div className="mx-auto hidden max-w-7xl items-end gap-5 px-6 py-3 lg:flex">
        <div className="flex flex-col justify-end pb-2"><Logo /></div>
        <div className="flex flex-1 flex-col">
          <span className="mb-1 h-[14px] text-[11px] leading-[14px]" style={{ color: MUTED }}>Buy Second-Hand</span>
          <SearchBar />
        </div>
        <div className="flex shrink-0 flex-col items-center">
          <span className="mb-1 h-[14px] text-center text-[11px] leading-[14px]" style={{ color: MUTED }}>Sell Second-Hand</span>
          <div className="relative">
            {user?.is_suspended ? (
              <Button disabled data-testid="nav-sell" className="h-10 cursor-not-allowed rounded-full bg-slate-300 px-5 font-bold text-slate-500 hover:bg-slate-300">
                Sell with AI
              </Button>
            ) : (
              <>
                <Button asChild data-testid="nav-sell" className="h-10 rounded-full bg-white px-5 font-bold hover:bg-white/90" style={{ color: DEEP }}>
                  <Link to="/sell">Sell with AI</Link>
                </Button>
                <CursorTap />
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3 pb-1.5">
          {user ? (
            <>
              <NotifBell notifs={notifs} unread={unread} markRead={markRead} navigate={navigate} />
              <Button asChild variant="ghost" size="icon" className="rounded-full hover:bg-white/10" data-testid="nav-watchlist">
                <Link to="/watchlist" aria-label="Watchlist"><Heart className="h-5 w-5 text-white" /></Link>
              </Button>
              <Avatar user={user} navigate={navigate} logout={logout} />
            </>
          ) : (
            <LoggedOutIcons />
          )}
        </div>
      </div>

      {/* MOBILE (<1024px) */}
      <div className="lg:hidden">
        <div className="flex items-center justify-between px-4 pt-3">
          <Logo testid="logo-link-mobile" />
          <div className="flex items-center gap-2">
            {user?.is_suspended ? (
              <Button disabled data-testid="nav-sell-mobile" className="h-9 cursor-not-allowed rounded-full bg-slate-300 px-3 text-sm font-bold text-slate-500 hover:bg-slate-300">
                Sell with AI
              </Button>
            ) : (
              <Button asChild data-testid="nav-sell-mobile" className="h-9 rounded-full bg-white px-3 text-sm font-bold hover:bg-white/90" style={{ color: DEEP }}>
                <Link to="/sell">Sell with AI</Link>
              </Button>
            )}
            {user ? (
              <>
                <NotifBell notifs={notifs} unread={unread} markRead={markRead} navigate={navigate} />
                <Avatar user={user} navigate={navigate} logout={logout} />
              </>
            ) : (
              <LoggedOutIcons mobile />
            )}
          </div>
        </div>
        <div className="px-4 pb-3 pt-2">
          <SearchBar idSuffix="-mobile" />
        </div>
      </div>

      {(user?.is_suspended || user?.is_banned) && user?.deposit_status === "held" && (user?.deposit_balance ?? 0) < 49 ? (
        <div data-testid="suspension-banner" className="flex flex-wrap items-center justify-center gap-x-2 border-t border-amber-300 bg-amber-100 px-4 py-1.5 text-center text-xs font-medium leading-tight text-amber-900 sm:px-6">
          Your seller account is suspended, and your deposit is below AED 49 so your listings are paused. You can still browse and buy. Contact info@sellstuff.ai for help, and top up to at least AED 49 once your account is restored.
          <Link to="/deposit/topup" data-testid="deposit-paused-topup" className="font-semibold underline">Top up</Link>
        </div>
      ) : user?.is_suspended && (
        <div data-testid="suspension-banner" className="border-t border-amber-300 bg-amber-100 px-4 py-1.5 text-center text-xs font-medium leading-tight text-amber-900 sm:px-6">
          Your seller account is suspended. You can still browse and buy. Contact info@sellstuff.ai for help.
        </div>
      )}
      {!user?.is_suspended && !user?.is_banned && user?.deposit_status === "held" && (user?.deposit_balance ?? 0) < 49 && (
        <div data-testid="deposit-paused-banner" className="flex flex-wrap items-center justify-center gap-x-2 border-t border-red-300 bg-red-100 px-4 py-1.5 text-center text-xs font-medium leading-tight text-red-800 sm:px-6">
          Your listings are paused because your deposit is below AED 49. Top up to at least AED 49 to put them back online, or AED 99 to publish new listings.
          <Link to="/deposit/topup" data-testid="deposit-paused-topup" className="font-semibold underline">Top up now</Link>
        </div>
      )}
    </header>
  );
}
